import { Controller } from "@hotwired/stimulus";

// « Heures du mois » (18/10/2026), up to two hundred teachers.
// - autosave: each box is saved when it is left; the line, the teacher's
//   total and the counters follow the answer. A refused box turns red, the
//   others are not touched.
// - without autosave (the step before a generation): the boxes go with the
//   form, the amounts follow the typing.
// Enter moves to the next box, as Tab does, to type from a paper sheet.
export default class extends Controller {
  static targets = ["row", "input", "amount", "rowTotal", "verify", "count", "error", "saved", "flags", "progress"];

  static values = {
    autosave: Boolean,
    lineUrl: String,
    verifyUrl: String,
    period: String,
    generationId: String,
    failedMessage: String,
  };

  keydown(event) {
    if (event.key !== "Enter") return;

    event.preventDefault();
    const inputs = this.inputTargets;
    const next = inputs[inputs.indexOf(event.target) + 1];
    if (next) {
      next.focus();
      next.select();
    } else {
      event.target.blur();
    }
  }

  changed(event) {
    const input = event.target;
    if (!this.autosaveValue) {
      this.refreshLocally(input);
      return;
    }
    if (input.value.trim() === (input.dataset.savedValue || "").trim()) return;

    this.saveLine(input);
  }

  async saveLine(input) {
    const line = input.closest("tr");
    const body = this.params({ hourly_rate_id: input.dataset.hourlyRateId, hours: input.value });
    this.showState(line, null);
    try {
      const response = await this.send(this.lineUrlValue, body);
      const data = await response.json();
      if (!response.ok) {
        this.showState(line, data.error || this.failedMessageValue);
        return;
      }
      input.dataset.savedValue = input.value;
      line.dataset.source = data.source;
      this.setText(line, "amount", data.amount);
      this.toggleFlag(line, "entered", data.source === "entered");
      this.toggleFlag(line, "gap", data.gap);
      const row = line.closest("tbody");
      const total = row.querySelector("[data-teaching-hours-target='rowTotal']");
      if (total) total.textContent = data.row_amount;
      const box = row.querySelector("[data-teaching-hours-target='verify']");
      if (box) box.checked = data.verified;
      row.querySelector("[data-row-flag='zero']")?.classList.toggle("hidden", !data.row_zero);
      this.updateCounts(data.counts);
      this.showState(line, null, true);
    } catch (_error) {
      this.showState(line, this.failedMessageValue);
    }
  }

  async verify(event) {
    const box = event.target;
    const body = this.params({ employee_profile_id: box.dataset.employeeProfileId, verified: box.checked ? "1" : "0" });
    try {
      const response = await this.send(this.verifyUrlValue, body);
      if (!response.ok) throw new Error("refused");
      this.updateCounts((await response.json()).counts);
    } catch (_error) {
      box.checked = !box.checked;
      window.alert(this.failedMessageValue);
    }
  }

  // The step before a generation: no saving, the amounts follow the typing.
  refreshLocally(input) {
    const line = input.closest("tr");
    const typed = input.value.trim().replace(",", ".");
    const hours = typed === "" ? Number(input.dataset.counted) : Number(typed);
    const amount = Number.isFinite(hours) ? Math.round(hours * Number(input.dataset.rate)) : 0;
    this.setText(line, "amount", this.format(amount));
    const row = line.closest("tbody");
    let total = 0;
    row.querySelectorAll("[data-teaching-hours-target='amount']").forEach((cell) => {
      total += Number(cell.textContent.replace(/[^\d]/g, "")) || 0;
    });
    const totalTarget = row.querySelector("[data-teaching-hours-target='rowTotal']");
    if (totalTarget) totalTarget.textContent = this.format(total);
  }

  params(fields) {
    const body = new FormData();
    body.append("period", this.periodValue);
    if (this.generationIdValue) body.append("generation_id", this.generationIdValue);
    Object.entries(fields).forEach(([key, value]) => body.append(key, value));
    return body;
  }

  send(url, body) {
    const token = document.querySelector("meta[name='csrf-token']")?.content;
    return fetch(url, {
      method: "PATCH",
      body,
      headers: { Accept: "application/json", "X-CSRF-Token": token },
      credentials: "same-origin",
    });
  }

  showState(line, error, saved = false) {
    const input = line.querySelector("[data-teaching-hours-target='input']");
    const errorTarget = line.querySelector("[data-teaching-hours-target='error']");
    const savedTarget = line.querySelector("[data-teaching-hours-target='saved']");
    input?.classList.toggle("ds-input--error", Boolean(error));
    if (errorTarget) {
      errorTarget.textContent = error || "";
      errorTarget.classList.toggle("hidden", !error);
    }
    savedTarget?.classList.toggle("hidden", !saved);
  }

  toggleFlag(line, flag, shown) {
    line.querySelector(`[data-flag='${flag}']`)?.classList.toggle("hidden", !shown);
  }

  setText(line, target, text) {
    const element = line.querySelector(`[data-teaching-hours-target='${target}']`);
    if (element) element.textContent = text;
  }

  updateCounts(counts) {
    if (!counts) return;
    this.countTargets.forEach((element) => {
      const value = counts[element.dataset.status];
      if (value !== undefined) element.textContent = value;
    });
    if (!this.hasProgressTarget) return;
    const checkable = Math.max(counts.all - counts.locked, 1);
    const fill = this.progressTarget.querySelector(".ds-progress__fill");
    if (fill) fill.style.width = `${Math.min((counts.verified / checkable) * 100, 100).toFixed(1)}%`;
  }

  format(amount) {
    return `${String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} F CFA`;
  }
}
